import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Plus, Pencil, Trash2, X, AlertTriangle, CheckCircle2 } from "lucide-react";

const inputCls =
  "w-full rounded-xl border border-[color:var(--hairline)] bg-white px-3 py-2.5 text-sm outline-none focus:border-[color:var(--ink)] transition-colors duration-200";

export const AdminSimulations = () => {
  const [sims, setSims] = useState(null);
  const [editor, setEditor] = useState(null); // full sim detail with questions
  const [pool, setPool] = useState([]); // all questions (for adding)
  const [titleDialog, setTitleDialog] = useState(null); // {mode:'create'|'rename', id?, titolo}
  const [deleting, setDeleting] = useState(null);
  const [addBlock, setAddBlock] = useState(null); // block number for the add-question picker
  const [addSearch, setAddSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [trial, setTrial] = useState(null); // {sampleQuestionCount, availableQuestions, perBlock}
  const [trialCount, setTrialCount] = useState("");
  const [trialBusy, setTrialBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/simulations").then((r) => setSims(r.data)).catch(() => toast.error("Impossibile caricare le simulazioni."));
    api.get("/admin/settings/trial").then((r) => {
      setTrial(r.data);
      setTrialCount(String(r.data.sampleQuestionCount));
    }).catch(() => {});
  }, []);

  const saveTrial = async () => {
    const n = parseInt(trialCount, 10);
    if (Number.isNaN(n)) {
      toast.error("Inserisci un numero valido.");
      return;
    }
    setTrialBusy(true);
    try {
      await api.put("/admin/settings/trial", { sampleQuestionCount: n });
      toast.success(`Simulazione campione: ${n} domande.`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTrialBusy(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/admin/questions").then((r) => setPool(r.data)).catch(() => {});
  }, [load]);

  const openEditor = async (simId) => {
    try {
      const r = await api.get(`/admin/simulations/${simId}`);
      setEditor(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveTitle = async () => {
    if (!titleDialog.titolo.trim()) {
      toast.error("Il titolo è obbligatorio.");
      return;
    }
    setBusy(true);
    try {
      if (titleDialog.mode === "create") {
        await api.post("/admin/simulations", { titolo: titleDialog.titolo, ordine: (sims?.length || 0) + 1, questionIds: [] });
        toast.success("Simulazione creata. Ora aggiungi 15 domande per blocco.");
      } else {
        const sim = sims.find((s) => s.id === titleDialog.id);
        await api.put(`/admin/simulations/${titleDialog.id}`, { titolo: titleDialog.titolo, ordine: sim?.ordine || 0, questionIds: sim?.questionIds || [] });
        toast.success("Titolo aggiornato.");
      }
      setTitleDialog(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const persistEditor = async (nextQuestionIds, successMsg) => {
    setBusy(true);
    try {
      await api.put(`/admin/simulations/${editor.id}`, { titolo: editor.titolo, ordine: editor.ordine || 0, questionIds: nextQuestionIds });
      toast.success(successMsg);
      const r = await api.get(`/admin/simulations/${editor.id}`);
      setEditor(r.data);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const removeQuestion = (qid) => {
    const next = editor.questionIds.filter((id) => id !== qid);
    persistEditor(next, "Domanda rimossa dalla simulazione.");
  };

  const addQuestion = (qid) => {
    const next = [...editor.questionIds, qid];
    persistEditor(next, "Domanda aggiunta alla simulazione.");
    setAddBlock(null);
    setAddSearch("");
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/simulations/${deleting.id}`);
      toast.success("Simulazione eliminata.");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const addCandidates = useMemo(() => {
    if (!editor || addBlock === null) return [];
    const included = new Set(editor.questionIds);
    const q = addSearch.trim().toLowerCase();
    return pool
      .filter((p) => p.blocco === addBlock && !included.has(p.id))
      .filter((p) => !q || p.testo.toLowerCase().includes(q))
      .slice(0, 40);
  }, [editor, addBlock, addSearch, pool]);

  // ---------- EDITOR VIEW ----------
  if (editor) {
    const byBlock = { 1: [], 2: [], 3: [], 4: [] };
    editor.questions.forEach((q) => byBlock[q.blocco]?.push(q));
    return (
      <div data-testid="admin-simulation-editor">
        <button
          onClick={() => setEditor(null)}
          data-testid="admin-simulation-editor-back"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--text-2)] hover:text-[color:var(--ink)] transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" /> Simulazioni
        </button>

        <div className="mt-4 rounded-3xl border border-[color:var(--hairline)] bg-white p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-extrabold tracking-tight text-lg">{editor.titolo}</h3>
            {editor.valid ? (
              <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide rounded-full bg-[color:var(--volt)] px-2.5 py-1">
                <CheckCircle2 className="w-3 h-3" /> Valida — 60 domande
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide rounded-full border border-[color:var(--danger)]/40 text-[color:var(--danger)] px-2.5 py-1" data-testid="admin-simulation-invalid-badge">
                <AlertTriangle className="w-3 h-3" /> Incompleta — non avviabile
              </span>
            )}
          </div>
          <p className="mt-2 font-mono text-xs text-[color:var(--text-2)]">
            {[1, 2, 3, 4].map((b) => `B${b}: ${editor.blockCounts[String(b)]}/15`).join(" · ")}
          </p>
        </div>

        {[1, 2, 3, 4].map((b) => (
          <div key={b} className="mt-4 rounded-3xl border border-[color:var(--hairline)] bg-white p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h4 className="text-xs font-extrabold tracking-[0.14em] uppercase">
                Blocco {b} <span className={`font-mono ${byBlock[b].length === 15 ? "text-[color:var(--text-2)]" : "text-[color:var(--danger)]"}`}>({byBlock[b].length}/15)</span>
              </h4>
              <button
                onClick={() => {
                  setAddBlock(b);
                  setAddSearch("");
                }}
                data-testid={`admin-sim-add-question-block-${b}-button`}
                className="inline-flex items-center gap-1 rounded-full border border-[color:var(--hairline)] px-3 py-1.5 text-[11px] font-bold hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
              >
                <Plus className="w-3 h-3" /> Aggiungi
              </button>
            </div>
            <div className="space-y-1.5">
              {byBlock[b].length === 0 && <p className="text-xs text-[color:var(--text-2)]">Nessuna domanda per questo blocco.</p>}
              {byBlock[b].map((q, i) => (
                <div key={q.id} className="flex items-start gap-2.5 rounded-xl border border-[color:var(--hairline)] px-3 py-2">
                  <span className="font-mono text-[10px] text-[color:var(--text-2)] pt-0.5 w-5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-[13px] leading-5 flex-1 min-w-0">{q.testo}</p>
                  <button
                    onClick={() => removeQuestion(q.id)}
                    disabled={busy}
                    aria-label="Rimuovi domanda"
                    data-testid="admin-sim-remove-question-button"
                    className="shrink-0 w-7 h-7 rounded-full border border-[color:var(--hairline)] flex items-center justify-center text-[color:var(--danger)] hover:bg-red-50 disabled:opacity-40 transition-colors duration-200"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Add question picker */}
        <Dialog open={addBlock !== null} onOpenChange={(o) => !o && setAddBlock(null)}>
          <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-extrabold tracking-tight">Aggiungi domanda — Blocco {addBlock}</DialogTitle>
              <DialogDescription>Scegli dall'archivio. Puoi creare nuove domande dal tab "Domande".</DialogDescription>
            </DialogHeader>
            <input
              className={inputCls}
              placeholder="Cerca nel testo…"
              value={addSearch}
              onChange={(e) => setAddSearch(e.target.value)}
              data-testid="admin-sim-add-search-input"
            />
            <div className="space-y-1.5 mt-2">
              {addCandidates.length === 0 && <p className="text-sm text-[color:var(--text-2)] py-4 text-center">Nessuna domanda disponibile per questo blocco.</p>}
              {addCandidates.map((q) => (
                <button
                  key={q.id}
                  onClick={() => addQuestion(q.id)}
                  disabled={busy}
                  data-testid="admin-sim-add-candidate-button"
                  className="w-full text-left rounded-xl border border-[color:var(--hairline)] px-3 py-2.5 text-[13px] leading-5 hover:bg-[color:var(--bg-subtle)] disabled:opacity-50 transition-colors duration-200"
                >
                  {q.testo}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---------- LIST VIEW ----------
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-xs text-[color:var(--text-2)] leading-5">
          Ogni simulazione deve avere <span className="font-mono font-semibold text-[color:var(--ink)]">60 domande, 15 per blocco</span>, altrimenti non è avviabile dagli studenti.
        </p>
        <button
          onClick={() => setTitleDialog({ mode: "create", titolo: "" })}
          data-testid="admin-create-simulation-button"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 hover:bg-black/85 transition-colors duration-200"
        >
          <Plus className="w-3.5 h-3.5" /> Nuova simulazione
        </button>
      </div>

      {!sims && <Skeleton className="h-40 rounded-3xl" />}
      <div className="space-y-2" data-testid="admin-simulations-list">
        {sims?.map((s) => (
          <div key={s.id} className="rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold">{s.titolo}</p>
                  {s.valid ? (
                    <span className="text-[9px] font-extrabold uppercase rounded-full bg-[color:var(--volt)] px-2 py-0.5">Valida</span>
                  ) : (
                    <span className="text-[9px] font-extrabold uppercase rounded-full border border-[color:var(--danger)]/40 text-[color:var(--danger)] px-2 py-0.5">Incompleta</span>
                  )}
                </div>
                <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-1">
                  {s.questionCount} domande · {[1, 2, 3, 4].map((b) => `B${b}:${s.blockCounts[String(b)]}`).join(" ")}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => openEditor(s.id)}
                  data-testid="admin-edit-simulation-button"
                  className="rounded-full border border-[color:var(--hairline)] px-3.5 py-2 text-xs font-bold hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                >
                  Gestisci domande
                </button>
                <button
                  onClick={() => setTitleDialog({ mode: "rename", id: s.id, titolo: s.titolo })}
                  aria-label="Rinomina simulazione"
                  data-testid="admin-rename-simulation-button"
                  className="w-9 h-9 rounded-full border border-[color:var(--hairline)] flex items-center justify-center hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setDeleting(s)}
                  aria-label="Elimina simulazione"
                  data-testid="admin-delete-simulation-button"
                  className="w-9 h-9 rounded-full border border-[color:var(--hairline)] flex items-center justify-center text-[color:var(--danger)] hover:bg-red-50 transition-colors duration-200"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Trial sample simulation settings */}
      <div className="mt-6 rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5" data-testid="admin-trial-settings-card">
        <h3 className="text-xs font-extrabold tracking-[0.14em] uppercase">Simulazione campione (trial gratuito)</h3>
        <p className="mt-2 text-xs text-[color:var(--text-2)] leading-5">
          L'assaggio gratuito per gli utenti free, con domande dedicate che non toccano le simulazioni a pagamento.
          {trial && (
            <span className="font-mono"> Disponibili: {trial.availableQuestions} ({[1, 2, 3, 4].map((b) => `B${b}:${trial.perBlock?.[String(b)] ?? 0}`).join(" ")})</span>
          )}
        </p>
        <div className="mt-3 flex items-end gap-3 flex-wrap">
          <div>
            <label htmlFor="trial-count-input" className="block text-[10px] uppercase tracking-wide font-bold text-[color:var(--text-2)] mb-1">
              Domande per assaggio
            </label>
            <input
              id="trial-count-input"
              type="number"
              min="4"
              max={trial?.availableQuestions || 20}
              value={trialCount}
              onChange={(e) => setTrialCount(e.target.value)}
              data-testid="admin-trial-count-input"
              className={`${inputCls} w-28`}
            />
          </div>
          <button
            onClick={saveTrial}
            disabled={trialBusy || !trial}
            data-testid="admin-save-trial-settings-button"
            className="rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-5 py-2.5 min-h-[40px] hover:bg-black/85 disabled:opacity-60 transition-colors duration-200"
          >
            {trialBusy ? "Salvataggio…" : "Salva"}
          </button>
        </div>
        <p className="mt-2 font-mono text-[11px] text-[color:var(--text-2)]">Le domande vengono distribuite equamente sui 4 blocchi (n ÷ 4).</p>
      </div>

      {/* Title dialog */}
      <Dialog open={!!titleDialog} onOpenChange={(o) => !o && setTitleDialog(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-extrabold tracking-tight">{titleDialog?.mode === "create" ? "Nuova simulazione" : "Rinomina simulazione"}</DialogTitle>
            <DialogDescription>{titleDialog?.mode === "create" ? "Verrà creata vuota: aggiungi poi 15 domande per blocco." : "Modifica il titolo visibile agli studenti."}</DialogDescription>
          </DialogHeader>
          {titleDialog && (
            <input
              className={inputCls}
              value={titleDialog.titolo}
              onChange={(e) => setTitleDialog({ ...titleDialog, titolo: e.target.value })}
              placeholder="Es. Simulazione 06"
              data-testid="admin-simulation-title-input"
            />
          )}
          <DialogFooter>
            <button onClick={() => setTitleDialog(null)} className="rounded-full border border-[color:var(--hairline)] px-5 py-2.5 font-semibold text-sm hover:bg-[color:var(--bg-subtle)] transition-colors duration-200">Annulla</button>
            <button onClick={saveTitle} disabled={busy} data-testid="admin-save-simulation-title-button" className="rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-2.5 text-sm hover:bg-black/85 disabled:opacity-60 transition-colors duration-200">
              {busy ? "Salvataggio…" : "Salva"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-extrabold tracking-tight">Eliminare "{deleting?.titolo}"?</AlertDialogTitle>
            <AlertDialogDescription>Le domande restano in archivio: viene eliminata solo la simulazione. Operazione irreversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="admin-confirm-delete-simulation-button" className="rounded-full bg-[color:var(--danger)] hover:bg-[color:var(--danger)]/90 font-extrabold">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
